let allowForceScrollTop = true;
let reenableTimer: ReturnType<typeof setTimeout> | undefined;
const pendingScrollTargets: number[] = [];
const scheduledTimeouts: number[] = [];
const scheduledAnimationFrames: number[] = [];
const DEFAULT_REENABLE_DELAY = 500;
const RETRY_DELAYS = [0, 16, 64, 120, 240];
 
export function setForceScrollTopEnabled(allow: boolean): void {
    allowForceScrollTop = allow;
 
    if (allow && reenableTimer) {
        clearTimeout(reenableTimer);
        reenableTimer = undefined;
    }
 
    if (allow) {
        drainPendingScrolls();
    }
}
 
export function suppressForceScrollTopTemporarily(duration?: number): void {
    setForceScrollTopEnabled(false);
 
    if (reenableTimer) {
        clearTimeout(reenableTimer);
    }
 
    const delay = typeof duration === 'number' && duration > 0 ? duration : DEFAULT_REENABLE_DELAY;
    reenableTimer = setTimeout(() => {
        setForceScrollTopEnabled(true);
    }, delay);
}
 
export function forceScrollTop(targetY = 0): void {
    if (!isBrowserContext()) {
        return;
    }
 
    if (!allowForceScrollTop) {
        queuePendingScroll(targetY);
        return;
    }
 
    performScrollSequence(targetY);
}
 
function queuePendingScroll(targetY: number): void {
    // Keep only the most recent request to avoid redundant work.
    pendingScrollTargets.length = 0;
    pendingScrollTargets.push(targetY);
}
 
function drainPendingScrolls(): void {
    if (!pendingScrollTargets.length) {
        return;
    }
 
    const targets = [...pendingScrollTargets];
    pendingScrollTargets.length = 0;
    targets.forEach(y => performScrollSequence(y));
}
 
function performScrollSequence(targetY: number): void {
    cancelScheduledScrolls();
 
    const applyScroll = () => applyScrollPosition(targetY);
 
    RETRY_DELAYS.forEach(delay => {
        if (delay === 0) {
            applyScroll();
        } else {
            scheduleTimeout(applyScroll, delay);
        }
    });
 
    if (typeof requestAnimationFrame === 'function') {
        scheduleAnimationFrame(() => applyScroll());
        scheduleAnimationFrame(() => scheduleAnimationFrame(() => applyScroll()));
    }
 
    Promise.resolve().then(() => applyScroll());
}
 
function applyScrollPosition(targetY: number): void {
    const doc = document;
    const htmlEl = doc.documentElement;
    const bodyEl = doc.body;
 
    const previousHtmlBehavior = htmlEl?.style.scrollBehavior ?? '';
    const previousBodyBehavior = bodyEl?.style.scrollBehavior ?? '';
 
    if (htmlEl) htmlEl.style.scrollBehavior = 'auto';
    if (bodyEl) bodyEl.style.scrollBehavior = 'auto';
 
    try {
        if (typeof window.scrollTo === 'function') {
            window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
            window.scrollTo(0, targetY);
        } else {
            window.scroll(0, targetY);
        }
    } catch {
        window.scroll(0, targetY);
    }
 
    const additionalTargets = collectScrollableElements();
 
    additionalTargets.forEach(target => {
        target.scrollTop = targetY;
        target.scrollLeft = 0;
    });
 
    const restoreBehavior = () => {
        if (htmlEl) htmlEl.style.scrollBehavior = previousHtmlBehavior;
        if (bodyEl) bodyEl.style.scrollBehavior = previousBodyBehavior;
    };
 
    if (typeof requestAnimationFrame === 'function') {
        scheduleAnimationFrame(() => restoreBehavior());
    } else {
        scheduleTimeout(restoreBehavior, 0);
    }
}
 
function collectScrollableElements(): HTMLElement[] {
    const doc = document;
    const elements = new Set<HTMLElement>();
 
    const addIfElement = (el: Element | null | undefined) => {
        if (el instanceof HTMLElement) {
            elements.add(el);
        }
    };
 
    addIfElement(doc.scrollingElement as HTMLElement | null);
    addIfElement(doc.documentElement);
    addIfElement(doc.body);
    addIfElement(doc.getElementById('otb-layout'));
    addIfElement(doc.getElementById('zoom-wrapper'));
 
    doc.querySelectorAll<HTMLElement>('[data-scroll-root], [data-scrollable], .layout, .div-content, .content, .main-content, .scroll-container').forEach(el => elements.add(el));
 
    return Array.from(elements);
}
 
function cancelScheduledScrolls(): void {
    while (scheduledTimeouts.length) {
        const handle = scheduledTimeouts.pop();
        if (handle !== undefined) {
            clearTimeout(handle);
        }
    }
 
    while (scheduledAnimationFrames.length) {
        const handle = scheduledAnimationFrames.pop();
        if (handle !== undefined && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(handle);
        }
    }
}
 
function scheduleTimeout(callback: () => void, delay: number): void {
    const handle = window.setTimeout(() => {
        try {
            callback();
        } finally {
            const index = scheduledTimeouts.indexOf(handle);
            if (index !== -1) {
                scheduledTimeouts.splice(index, 1);
            }
        }
    }, delay);
 
    scheduledTimeouts.push(handle);
}
 
function scheduleAnimationFrame(callback: () => void): void {
    if (typeof requestAnimationFrame !== 'function') {
        scheduleTimeout(callback, 0);
        return;
    }
 
    const handle = requestAnimationFrame(() => {
        try {
            callback();
        } finally {
            const index = scheduledAnimationFrames.indexOf(handle);
            if (index !== -1) {
                scheduledAnimationFrames.splice(index, 1);
            }
        }
    });
 
    scheduledAnimationFrames.push(handle);
}
 
function isBrowserContext(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
 
 